import {RcFile} from 'antd/lib/upload';
import {useEffect, useState} from 'react';

import {Spinner} from '../../../../../../layout/spinner/spinner';
import {useFormRules} from '../../../../../../service/form/form-rules-hook';
import {useUploadStoryImage} from '../../../../../../service/yandex-stories/yandex-stories-hook';
import {YandexStoryFormType} from '../../../../../../service/yandex-stories/yandex-stories-type';
import {FormInstance} from '../../../../../../typings/antd';
import {classNames} from '../../../../../../util/css';
import {noop} from '../../../../../../util/function';
import {useRefreshId} from '../../../../../../util/hook';
import {FileFormInput} from '../../../../../form/file-upload-item/file-form-input';
import {
    YANDEX_STORY__MAX_SLIDES,
    YANDEX_STORY_MAX_IMAGE_HEIGHT,
    YANDEX_STORY_MAX_IMAGE_WIDTH,
    YANDEX_STORY_MIN_IMAGE_HEIGHT,
    YANDEX_STORY_MIN_IMAGE_WIDTH,
} from '../../../yandex-story-form-helper';

import {appendYandexStorySlides, UploadedSlideFilesMapType} from './yandex-story-slide-upload-helper';
import * as styles from './yandex-story-form-slides-content-step.scss';

type PropsType = {
    formInstance: FormInstance<YandexStoryFormType>;
    slidesCount: number;
    isUploadBlocked: boolean;
    onUploadingChange: (isUploading: boolean) => void;
};

export function YandexStoryContentStepSlideUpload(props: PropsType): JSX.Element {
    const {formInstance, slidesCount, isUploadBlocked, onUploadingChange} = props;

    const {mutateAsync: uploadFile} = useUploadStoryImage();
    const {validateImageDimension} = useFormRules();

    const [fileList, setFileList] = useState<UploadedSlideFilesMapType>({});

    const isFileMapEmpty = Object.keys(fileList).length === 0;

    const {refresh, refreshId} = useRefreshId();

    // The budget of this batch, not the product limit: the files already in the input are added back,
    // so `FileUpload` keeps the trigger — and the spinner it renders — in place while they upload.
    const uploadMaxCount = Object.keys(fileList).length + YANDEX_STORY__MAX_SLIDES - slidesCount;

    useEffect(() => {
        onUploadingChange(!isFileMapEmpty);
    }, [isFileMapEmpty, onUploadingChange]);

    // Appending the last batch fills the rail and can unmount this input in the same commit, before the
    // effect above gets to report the batch as finished. Releasing the lock here covers that.
    useEffect(() => {
        return () => {
            onUploadingChange(false);
        };
    }, [onUploadingChange]);

    useEffect(() => {
        if (!isFileMapEmpty && Object.values(fileList).every((fileItem) => fileItem.status !== 'uploading')) {
            appendYandexStorySlides(formInstance, fileList);

            setFileList({});
            refresh();
        }
    }, [fileList, formInstance, isFileMapEmpty, refresh]);

    return (
        <FileFormInput
            key={refreshId}
            onChange={setFileList}
            remove={noop}
            showCustomUploadList={false}
            upload={uploadFile}
            uploadProps={{accept: '.png,.jpg', maxFileSize: 10, maxCount: uploadMaxCount}}
            validationRules={[
                async (file: RcFile) =>
                    validateImageDimension(file, {
                        min: {
                            height: YANDEX_STORY_MIN_IMAGE_HEIGHT,
                            width: YANDEX_STORY_MIN_IMAGE_WIDTH,
                        },
                        max: {
                            height: YANDEX_STORY_MAX_IMAGE_HEIGHT,
                            width: YANDEX_STORY_MAX_IMAGE_WIDTH,
                        },
                    }),
            ]}
            value={fileList}
        >
            <button
                className={classNames(
                    styles.YandexStoryFormSlidesContentStep_slide,
                    styles.YandexStoryFormSlidesContentStep_addSlideButton
                )}
                disabled={!isFileMapEmpty || isUploadBlocked}
                type="button"
            >
                {isFileMapEmpty ? '+' : <Spinner />}
            </button>
        </FileFormInput>
    );
}
